import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Row,
  Col,
  Form,
  Container,
  Button,
  Input,
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { parseISO } from "date-fns"
import "./QuotationPdfForm.css"
import "./PdfPreview/pdf-preview.css"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useNavigate, useParams } from "react-router-dom"
import { useReturnPath } from "hooks/useReturnPath"
import {
  useCreateQuotation,
  useEditQuotation,
  useQuotation,
} from "hooks/useQuotation"
import { toast } from "react-toastify"

import DynamicField from "components/Common/DynamicField"
import { useDynamicForm } from "hooks/useDynamicForm"
import { convertToWords, formatIndianNumber } from "helpers/number_helpers"
import { formatSinceDate } from "utils/formatDate"
import { CustomerSelector } from "./Quotation/Customer"
import { useCustomer, useCustomerSearchAPI } from "hooks/useCustomer"
import VehicleSpecification from "./Quotation/Vehicle/VehicleSpecification"
import { useMediaQuery } from "react-responsive"
import { prepareVehicleStockFromSpecs } from "helpers/quotation_helpers"
import { useListParams } from "hooks/useListParams"
import { useVehicleStock } from "hooks/stockHook"
import { useAuth } from "hooks/authHook"
import CompanyHeader from "./PdfPreview/CompanyHeader"
import { appendQueryParam } from "utils/urlParams"

const QuotationAddEdit = () => {
  document.title = "Quotation Form | Aurobil Cars"

  const isMobile = useMediaQuery({ maxWidth: 767 })

  const navigate = useNavigate()
  const { identifier } = useParams()
  const isEdit = !!identifier
  const { fromPath } = useReturnPath("/quotation-list")
  const [vehicleSpecs, setVehicleSpecs] = useState([])
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const { user } = useAuth()

  const DEFAULT_VEHICLE_SPEC_KEYS = useMemo(
    () => [
      { key: "Vehicle Name", type: "text" },
      { key: "Chassis No", type: "text" },
      { key: "Engine No", type: "text" },
      { key: "Model", type: "text" },
      { key: "Color", type: "text" },
      { key: "CC", type: "text" },
      { key: "Fuel Type", type: "text" },
      { key: "Descriptions", type: "textarea" },
    ],
    []
  )

  const { params } = useListParams()

  const { data: customerData, isLoading: customerLoading } = useCustomer(
    params.cuid,
    !!params.cuid
  )

  const { data: vehicleData, isLoading: vehicleLoading } = useVehicleStock(
    params.stock_id
  )

  const { data: quotationResponse, isLoading } = useQuotation(identifier, isEdit)
  const { mutateAsync: createQuotation, isPending: createLoading } =
    useCreateQuotation()
  const { mutateAsync: editQuotation, isPending: editLoading } =
    useEditQuotation()

  const {
    items: priceItems,
    addItem: addPriceItem,
    removeItem: removePriceItem,
    updateItem: updatePriceItem,
    resetItems: resetPriceItems,
  } = useDynamicForm([
    { id: "1", description: "Unit Price", amount: "" },
    { id: "2", description: "Registration Cost", amount: "" },
  ])

  const totalAmount = useMemo(() => {
    return priceItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0)
  }, [priceItems])

  const amountInWords = useMemo(() => {
    return convertToWords(totalAmount) + " Taka Only"
  }, [totalAmount])

  const EMPTY_QUOTATION = useMemo(() => ({}), [])
  const EMPTY_PDF_CONTENT = useMemo(() => ({}), [])

  const quotation = quotationResponse?.data ?? EMPTY_QUOTATION
  const quotationPdfContent = quotation?.pdf_content ?? EMPTY_PDF_CONTENT

  const currentUserName = useMemo(
    () => user?.name || user?.username || "",
    [user]
  )

  const currentUserDesignation = useMemo(() => {
    if (typeof user?.designation === "string") return user.designation
    if (user?.designation?.name) return user.designation.name
    return ""
  }, [user])

  const defaultSignatoryDesignation =
    quotationPdfContent?.signatory_designation || currentUserDesignation || ""

  const defaultSignatoryName = defaultSignatoryDesignation
    ? quotationPdfContent?.signatory_name || currentUserName || ""
    : ""

  const initialValues = useMemo(
    () => ({
      quotation_date: quotationPdfContent?.quotation_date
        ? parseISO(quotationPdfContent.quotation_date)
        : new Date(),
      valid_until: quotationPdfContent?.valid_until
        ? parseISO(quotationPdfContent.valid_until)
        : new Date(Date.now() + 90 * 24 * 60 * 60 * 1000),
      customer_info: quotationPdfContent?.customer_info || "",
      subject:
        quotationPdfContent?.subject ||
        "Quotation for 1 (One) unit reconditioned vehicle",
      body_text:
        quotationPdfContent?.body_text ||
        "Dear Sir,\nWe are pleased to provide you with a quotation for the reconditioned vehicle(s) you are interested in. Please find the details below:",
      payment_terms:
        quotationPdfContent?.payment_terms ||
        "• 50% advance payment to confirm booking\n• Balance payment before delivery\n• Payment via bank transfer or cash",
      closing_text:
        quotationPdfContent?.closing_text ||
        "<p>Please do not hesitate to contact us if you have any questions or require further information. Our sales representatives are here to assist you in making the best choice.</p><p>Thank you for considering <b><i>AUROBIL CARS</i></b> for your reconditioned vehicle purchase. We look forward to serving your automotive needs.</p>",
      signatory_name: defaultSignatoryName,
      customer_signatory_name:
        quotationPdfContent?.customer_signatory_name ||
        quotation?.customer?.name ||
        "",
      signatory_designation: defaultSignatoryDesignation,
    }),
    [
      quotation,
      quotationPdfContent,
      currentUserName,
      currentUserDesignation,
      defaultSignatoryName,
      defaultSignatoryDesignation,
    ]
  )

  const validationSchema = Yup.object({
    quotation_date: Yup.date().required("Quotation date is required"),
    customer_info: Yup.string()
      .required("Customer information is required")
      .test("is-not-empty", "Customer information is required", value => {
        if (!value) return false
        const textContent = value.replace(/<[^>]*>/g, "").trim()
        return textContent.length > 0
      }),
    subject: Yup.string().required("Subject is required"),
    body_text: Yup.string().required("Body text is required"),
    payment_terms: Yup.string().required("Payment terms are required"),
    closing_text: Yup.string().required("Closing text is required"),
    signatory_name: Yup.string().nullable(),
    signatory_designation: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async values => {
      try {
        const filteredVehicleSpecs = vehicleSpecs.filter(
          spec => spec.value.trim() !== ""
        )

        const preparedNewVehicle =
          prepareVehicleStockFromSpecs(filteredVehicleSpecs) ?? null

        if (!selectedVehicle?.id && filteredVehicleSpecs.length === 0) {
          toast.error("Vehicle information is required.")
          return
        }

        const normalizedSignatoryDesignation =
          values.signatory_designation?.trim() || ""
        const normalizedSignatoryName = normalizedSignatoryDesignation
          ? values.signatory_name?.trim() || ""
          : ""

        const toDateString = date => {
          if (!date) return ""
          const d = date instanceof Date ? date : new Date(date)
          if (Number.isNaN(d.getTime())) return ""
          const pad = n => n.toString().padStart(2, "0")
          return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
            d.getDate()
          )}`
        }

        const pdfContent = {
          ...values,
          quotation_date: toDateString(values.quotation_date),
          valid_until: toDateString(values.valid_until),
          signatory_name: normalizedSignatoryName,
          signatory_designation: normalizedSignatoryDesignation,
          vehicle_specs: filteredVehicleSpecs,
          price_items: priceItems,
          total_amount: totalAmount,
          amount_in_words: amountInWords,
        }

        const payload = {
          pdf_content: pdfContent,
          customer_id: selectedCustomer?.id ?? null,
          new_customer: selectedCustomer ?? null,
          vehicle_id: selectedVehicle?.id ?? null,
          new_vehicle: preparedNewVehicle,
        }

        const res = isEdit
          ? await editQuotation({ id: identifier, ...payload })
          : await createQuotation(payload)

        toast.success(
          res?.message ??
            `Quotation ${isEdit ? "updated" : "created"} successfully.`
        )
        if (isEdit) {
          const focusValue = res?.data?.qid ?? identifier
          navigate(appendQueryParam(fromPath, "q_focus", focusValue))
        } else {
          navigate("/quotation-list")
        }
      } catch (error) {
        console.error("Failed to save quotation:", error)
        toast.error("Failed to save quotation.")
      }
    },
    [
      isEdit,
      identifier,
      vehicleSpecs,
      priceItems,
      totalAmount,
      amountInWords,
      selectedCustomer,
      selectedVehicle,
      createQuotation,
      editQuotation,
      navigate,
      fromPath,
    ]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit,
  })

  useEffect(() => {
    const qDate = new Date(formik.values.quotation_date)
    const validUntil = new Date(formik.values.valid_until)

    if (formik.values.valid_until && validUntil < qDate) {
      formik.setFieldValue("valid_until", formik.values.quotation_date)
    }
  }, [formik.values.quotation_date, formik.values.valid_until])

  const setQuickValidUntil = (daysToAdd = 0) => {
    const baseDate = new Date(formik.values.quotation_date)
    const newDate = new Date(
      baseDate.getFullYear(),
      baseDate.getMonth(),
      baseDate.getDate() + daysToAdd
    )
    formik.setFieldValue("valid_until", newDate)
  }

  const getFieldError = fieldName => ({
    invalid: formik.touched[fieldName] && !!formik.errors[fieldName],
    feedback: formik.touched[fieldName] && formik.errors[fieldName],
  })

  const buttonText = useMemo(() => {
    const loading = createLoading || editLoading
    return `${isEdit ? "Save Changes" : "Create Quotation"}${
      loading ? "..." : ""
    }`
  }, [isEdit, createLoading, editLoading])

  const isSubmitDisabled = useMemo(
    () => createLoading || editLoading,
    [createLoading, editLoading]
  )

  const { customerSearchAPI, loading: isSearching } = useCustomerSearchAPI({
    all: true,
    limit: 5,
  })

  useEffect(() => {
    if (customerData?.data && !customerLoading) {
      setSelectedCustomer(customerData?.data)
    }
  }, [customerData?.data, customerLoading])

  useEffect(() => {
    if (!isEdit || customerData?.data || customerLoading) return
    if (quotation?.customer) {
      setSelectedCustomer(quotation.customer)
    }
  }, [isEdit, quotation?.customer, customerData?.data, customerLoading])

  useEffect(() => {
    if (vehicleData?.data && !vehicleLoading) {
      setSelectedVehicle(vehicleData?.data)
    }
  }, [vehicleData?.data, vehicleLoading])

  useEffect(() => {
    if (!isEdit || vehicleData?.data || vehicleLoading) return
    if (quotation?.vehicle) {
      setSelectedVehicle(quotation.vehicle)
    }
  }, [isEdit, quotation?.vehicle, vehicleData?.data, vehicleLoading])

  useEffect(() => {
    if (!isEdit) return

    const savedPriceItems = quotationPdfContent?.price_items
    const savedVehicleSpecs = quotationPdfContent?.vehicle_specs

    if (Array.isArray(savedPriceItems) && savedPriceItems.length > 0) {
      resetPriceItems(savedPriceItems)
    }

    const baseSpecs = DEFAULT_VEHICLE_SPEC_KEYS.map((entry, idx) => ({
      id: `default-${idx}`,
      key: entry.key,
      value: "",
      type: entry.type,
    }))

    if (Array.isArray(savedVehicleSpecs) && savedVehicleSpecs.length > 0) {
      const merged = [...savedVehicleSpecs]
      baseSpecs.forEach(defaultSpec => {
        const exists = merged.some(spec => spec?.key === defaultSpec.key)
        if (!exists) merged.push(defaultSpec)
      })
      setVehicleSpecs(merged)
    } else if (isEdit) {
      setVehicleSpecs(baseSpecs)
    }
  }, [
    isEdit,
    quotationPdfContent?.price_items,
    quotationPdfContent?.vehicle_specs,
    resetPriceItems,
    DEFAULT_VEHICLE_SPEC_KEYS,
  ])

  const quickButtons = [
    { label: "Today", days: 0 },
    { label: "+1W", days: 7 },
    { label: "+1M", days: 30 },
    { label: "+2M", days: 60 },
    { label: "+3M", days: 90 },
  ]

  return (
    <div className="page-content" style={{ overflowX: "hidden", boxSizing: "border-box" }}>
      <Container fluid style={{ maxWidth: "100%", paddingLeft: 0, paddingRight: 0 }}>
        <Breadcrumbs title="Quotations" breadcrumbItem="Add / Edit Quotation" />

        {/* Sticky action toolbar */}
        <div className="qpf-toolbar">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <small className="text-muted">Valid Until quick set:</small>
            {quickButtons.map((b, idx) => (
              <Button
                key={idx}
                type="button"
                color="outline-secondary"
                size="sm"
                onClick={() => setQuickValidUntil(b.days)}
              >
                {b.label}
              </Button>
            ))}
          </div>
          <div className="d-flex align-items-center gap-2">
            <Button
              type="button"
              color="outline-danger"
              size={isMobile ? "sm" : "md"}
              onClick={() => navigate(fromPath)}
            >
              <i className="bx bx-x me-1" />
              Cancel
            </Button>
            <Button
              type="button"
              color="primary"
              size={isMobile ? "sm" : "md"}
              disabled={isSubmitDisabled || isLoading}
              onClick={formik.handleSubmit}
            >
              <i className="bx bx-check me-1" />
              {buttonText}
            </Button>
          </div>
        </div>

        {/* PDF-style paper */}
        <Form onSubmit={formik.handleSubmit}>
          <div className="qpf-shell">
            <div className="qpf-paper pdf-preview">
              {/* Header: company info + title + meta */}
              <table className="qpf-header-table">
                <tbody>
                  <tr>
                    <td className="qpf-w-60 pdf-vt">
                      <CompanyHeader />
                    </td>
                    <td className="qpf-w-40 pdf-vt">
                      <table className="pdf-mb-5">
                        <tbody>
                          <tr>
                            <td className="pdf-text-right">
                              <span className="pdf-brand pdf-fs-22 pdf-text-red">
                                QUOTATION
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <table className="qpf-meta-table">
                        <tbody>
                          <tr>
                            <td>Quotation No.</td>
                            <td className="qpf-meta-value">
                              {quotation?.qid || (
                                <span className="text-muted">
                                  Auto-generated
                                </span>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td>Date</td>
                            <td className="qpf-meta-value">
                              <DatePicker
                                name="quotation_date"
                                selected={formik.values.quotation_date}
                                onChange={date =>
                                  formik.setFieldValue("quotation_date", date)
                                }
                                onBlur={formik.handleBlur}
                                dateFormat="MMMM d, yyyy"
                                className={`form-control qpf-inline ${
                                  getFieldError("quotation_date").invalid
                                    ? "is-invalid"
                                    : ""
                                }`}
                                wrapperClassName="w-100"
                                autoComplete="off"
                              />
                              {getFieldError("quotation_date").feedback && (
                                <div className="invalid-feedback d-block">
                                  {getFieldError("quotation_date").feedback}
                                </div>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              Valid Until
                              <div
                                className="text-muted"
                                style={{ fontSize: 10 }}
                              >
                                (
                                {formatSinceDate(
                                  formik.values.quotation_date,
                                  formik.values.valid_until
                                )}
                                )
                              </div>
                            </td>
                            <td className="qpf-meta-value">
                              <DatePicker
                                name="valid_until"
                                selected={formik.values.valid_until}
                                onChange={date =>
                                  formik.setFieldValue("valid_until", date)
                                }
                                onBlur={formik.handleBlur}
                                minDate={formik.values.quotation_date}
                                dateFormat="MMMM d, yyyy"
                                className={`form-control qpf-inline ${
                                  getFieldError("valid_until").invalid
                                    ? "is-invalid"
                                    : ""
                                }`}
                                wrapperClassName="w-100"
                                autoComplete="off"
                              />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Recipient (To) */}
              <div className="qpf-recipient">
                <CustomerSelector
                  formik={formik}
                  apiSearchFunction={customerSearchAPI}
                  isSearching={isSearching || customerLoading}
                  selectedCustomer={selectedCustomer}
                  setSelectedCustomer={setSelectedCustomer}
                  existCustomerData={
                    customerData?.data || quotation?.customer
                  }
                  isEdit={isEdit}
                  lockCustomer={!!params.cuid}
                  preserveCustomerInfo={!!quotationPdfContent?.customer_info}
                />
              </div>

              {/* Subject */}
              <div className="qpf-subject">
                <span className="qpf-subject-label pdf-brand">Sub:</span>
                <Input
                  type="text"
                  name="subject"
                  className="qpf-inline"
                  value={formik.values.subject}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Quotation subject..."
                  {...getFieldError("subject")}
                />
              </div>

              {/* Body intro (salutation) */}
              <div className="mb-3">
                <DynamicField
                  type="richtext"
                  name="body_text"
                  value={formik.values.body_text}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  setFieldValue={formik.setFieldValue}
                  placeholder="Salutation / intro..."
                  required
                  height={90}
                  invalid={
                    formik.touched.body_text &&
                    Boolean(formik.errors.body_text)
                  }
                  feedback={
                    formik.touched.body_text && formik.errors.body_text
                  }
                />
              </div>

              {/* Vehicle Specifications */}
              <div className="qpf-section-block">
                <div className="qpf-section-title">Vehicle Specifications</div>
                <VehicleSpecification
                  onVehicleSpecsChange={setVehicleSpecs}
                  initialVehicleSpecs={vehicleSpecs}
                  selectedVehicle={selectedVehicle}
                  setSelectedVehicle={setSelectedVehicle}
                  existVehicleData={vehicleData?.data}
                  existVehicleLoading={vehicleLoading}
                />
              </div>

              {/* Price Breakdown */}
              <div className="qpf-section-block">
                <div className="qpf-section-title">Price Breakdown</div>
                <table className="qpf-detail qpf-price-table">
                  <tbody>
                    {priceItems.map((item, idx) => (
                      <tr key={item.id}>
                        <td className="qpf-s" data-label="#">
                          {idx + 1}
                        </td>
                        <td className="qpf-l" data-label="Description">
                          <Input
                            type="text"
                            className="qpf-cell"
                            value={item.description}
                            onChange={e =>
                              updatePriceItem(
                                item.id,
                                "description",
                                e.target.value
                              )
                            }
                            placeholder="Item description"
                          />
                        </td>
                        <td className="qpf-c">:</td>
                        <td className="qpf-v" data-label="Amount (BDT)">
                          <Input
                            type="number"
                            className="qpf-cell qpf-amount"
                            value={item.amount}
                            onChange={e =>
                              updatePriceItem(
                                item.id,
                                "amount",
                                e.target.value
                              )
                            }
                            placeholder="Amount"
                            min="0"
                            step="0.01"
                          />
                        </td>
                        <td className="qpf-row-actions">
                          {priceItems.length > 1 && (
                            <Button
                              type="button"
                              color="link"
                              className="text-danger"
                              onClick={() => removePriceItem(item.id)}
                            >
                              <i className="bx bx-trash" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr className="qpf-add-row">
                      <td colSpan={5}>
                        <Button
                          type="button"
                          color="link"
                          size="sm"
                          onClick={() =>
                            addPriceItem({ description: "", amount: 0 })
                          }
                        >
                          <i className="bx bx-plus me-1" />
                          Add Cost Item
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Total + In Words + Payment Terms */}
              <table className="qpf-detail qpf-summary-table">
                <tbody>
                  <tr className="qpf-total-row">
                    <td className="qpf-s" />
                    <td className="qpf-l" data-label="">Total</td>
                    <td className="qpf-c">:</td>
                    <td className="qpf-v qpf-a">
                      BDT {formatIndianNumber(totalAmount)}
                    </td>
                  </tr>
                  <tr>
                    <td className="qpf-s" />
                    <td className="qpf-l" data-label="">In Words</td>
                    <td className="qpf-c">:</td>
                    <td className="qpf-v">{amountInWords}</td>
                  </tr>
                  <tr>
                    <td className="qpf-s" />
                    <td className="qpf-l" data-label="">Payment</td>
                    <td className="qpf-c">:</td>
                    <td className="qpf-v">
                      <DynamicField
                        type="richtext"
                        name="payment_terms"
                        value={formik.values.payment_terms}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        setFieldValue={formik.setFieldValue}
                        placeholder="Payment terms..."
                        required
                        height={70}
                        invalid={
                          formik.touched.payment_terms &&
                          Boolean(formik.errors.payment_terms)
                        }
                        feedback={
                          formik.touched.payment_terms &&
                          formik.errors.payment_terms
                        }
                      />
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Closing remarks */}
              <div className="qpf-closing">
                <DynamicField
                  type="richtext"
                  name="closing_text"
                  value={formik.values.closing_text}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  setFieldValue={formik.setFieldValue}
                  placeholder="Closing remarks..."
                  required
                  height={100}
                  invalid={
                    formik.touched.closing_text &&
                    Boolean(formik.errors.closing_text)
                  }
                  feedback={
                    formik.touched.closing_text &&
                    formik.errors.closing_text
                  }
                />
              </div>

              {/* Signature footer */}
              <table className="qpf-sig-table">
                <tbody>
                  <tr>
                    <td>
                      <div className="qpf-sig-sincerely">Sincerely,</div>
                      <div className="qpf-sig-divider">
                        <div className="qpf-sig-cell">
                          <Input
                            type="text"
                            name="signatory_name"
                            className="qpf-inline"
                            value={formik.values.signatory_name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="Signatory name"
                            {...getFieldError("signatory_name")}
                          />
                          <Input
                            type="text"
                            name="signatory_designation"
                            className="qpf-inline qpf-sig-sub"
                            value={formik.values.signatory_designation}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="Designation"
                            {...getFieldError("signatory_designation")}
                          />
                          <small className="text-muted">
                            of <span className="pdf-brand">AUROBIL CARS</span>
                          </small>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="qpf-footer-note">
                Thank you for choosing AUROBIL CARS.
              </div>
            </div>
          </div>

          {/* Bottom action row */}
          <Row className="mt-3 mb-4">
            <Col xs={12} className="text-center">
              <div className="d-flex flex-column flex-sm-row justify-content-center gap-2">
                <Button
                  type="button"
                  color="outline-danger"
                  size={isMobile ? "sm" : "md"}
                  onClick={() => navigate(fromPath)}
                >
                  <i className="bx bx-x me-2" />
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  size={isMobile ? "sm" : "md"}
                  disabled={isSubmitDisabled}
                >
                  <i className="bx bx-check me-2" />
                  {buttonText}
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  )
}

export default QuotationAddEdit
